import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, Building2, MapPin, Calendar, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgenceurResponsePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get('action'); // "valide" or "refuse"

  const [loading, setLoading] = useState(true);
  const [dossier, setDossier] = useState(null);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(preselect === 'refuse' ? 'refuse' : preselect === 'valide' ? 'valide' : '');
  const [comment, setComment] = useState('');
  const [dateModifiee, setDateModifiee] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/dossiers-agenceur/respond/${token}`);
        if (res.ok) {
          const data = await res.json();
          setDossier(data);
          if (data.already_responded) {
            setSubmitted(true);
            setResult({ status: data.status, comment: data.response_comment });
          }
        } else {
          const err = await res.json();
          setError(err.detail || 'Lien invalide ou expiré');
        }
      } catch {
        setError('Erreur de connexion');
      }
      finally { setLoading(false); }
    })();
  }, [token]);

  const handleSubmit = async () => {
    if (!decision) return;
    if (decision === 'refuse' && !comment.trim()) {
      alert('Le commentaire est obligatoire en cas de refus.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/dossiers-agenceur/respond/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, comment, date_modifiee: dateModifiee || null }),
      });
      if (res.ok) {
        const data = await res.json();
        setSubmitted(true);
        setResult({ status: data.status, message: data.message });
      } else {
        const err = await res.json();
        alert(err.detail || 'Erreur');
      }
    } catch {
      alert('Erreur réseau');
    }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-red-700 mb-2">Lien invalide</h2>
            <p className="text-sm text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    const isValide = result?.status?.includes('valide');
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className={`max-w-md w-full ${isValide ? 'border-green-200' : 'border-red-200'}`}>
          <CardContent className="p-8 text-center">
            {isValide
              ? <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
              : <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            }
            <h2 className={`text-xl font-bold mb-2 ${isValide ? 'text-green-700' : 'text-red-700'}`}>
              Commande {isValide ? 'Validée' : 'Refusée'}
            </h2>
            <p className="text-sm text-slate-600">Votre réponse a été enregistrée. L'équipe Wasabi a été notifiée.</p>
            {dossier && (
              <p className="text-sm text-slate-500 mt-3">{dossier.enseigne} — {dossier.ville}</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="agenceur-response-page">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="bg-orange-500 text-white p-6 rounded-t-xl">
          <div className="flex items-center gap-2 mb-1">
            <img src="/wasabi-logo.png" alt="Wasabi" className="h-7 brightness-0 invert" onError={e => e.target.style.display = 'none'} />
            <span className="text-sm opacity-80">CAPEX by Wasabi</span>
          </div>
          <h1 className="text-xl font-bold mt-2">Validation Commande Corner</h1>
        </div>

        {/* Dossier info */}
        <Card className="rounded-none border-x">
          <CardContent className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                <div><p className="text-slate-500 text-xs">Enseigne</p><p className="font-semibold">{dossier.enseigne}</p></div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                <div><p className="text-slate-500 text-xs">Ville</p><p className="font-semibold">{dossier.ville}</p></div>
              </div>
              <div className="flex items-start gap-2">
                <User className="w-4 h-4 text-slate-400 mt-0.5" />
                <div><p className="text-slate-500 text-xs">Responsable</p><p className="font-semibold">{dossier.responsable_wasabi || '-'}</p></div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                <div><p className="text-slate-500 text-xs">Date prévisionnelle</p><p className="font-semibold">{dossier.date_previsionnelle || '-'}</p></div>
              </div>
            </div>
            {dossier.adresse && (
              <p className="text-xs text-slate-500 border-t pt-2">{dossier.adresse}</p>
            )}
            {dossier.files_count > 0 && (
              <p className="text-xs text-slate-500">{dossier.files_count} pièce(s) jointe(s)</p>
            )}
          </CardContent>
        </Card>

        {/* Response Form */}
        <Card className="rounded-none rounded-b-xl border-x border-b">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Votre décision</Label>
              <div className="flex gap-3">
                <button type="button" onClick={() => setDecision('valide')}
                  className={`flex-1 p-4 rounded-xl border-2 text-center transition-all ${
                    decision === 'valide'
                      ? 'border-green-500 bg-green-50 shadow-sm'
                      : 'border-slate-200 hover:border-green-300 hover:bg-green-50/50'
                  }`} data-testid="btn-valide">
                  <CheckCircle2 className={`w-8 h-8 mx-auto mb-1 ${decision === 'valide' ? 'text-green-600' : 'text-slate-300'}`} />
                  <p className={`text-sm font-semibold ${decision === 'valide' ? 'text-green-700' : 'text-slate-500'}`}>Validée</p>
                </button>
                <button type="button" onClick={() => setDecision('refuse')}
                  className={`flex-1 p-4 rounded-xl border-2 text-center transition-all ${
                    decision === 'refuse'
                      ? 'border-red-500 bg-red-50 shadow-sm'
                      : 'border-slate-200 hover:border-red-300 hover:bg-red-50/50'
                  }`} data-testid="btn-refuse">
                  <XCircle className={`w-8 h-8 mx-auto mb-1 ${decision === 'refuse' ? 'text-red-600' : 'text-slate-300'}`} />
                  <p className={`text-sm font-semibold ${decision === 'refuse' ? 'text-red-700' : 'text-slate-500'}`}>Refusée</p>
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm text-slate-700">
                Commentaire {decision === 'refuse' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea value={comment} onChange={e => setComment(e.target.value)}
                placeholder={decision === 'refuse' ? 'Merci de préciser le motif de refus...' : 'Optionnel'}
                rows={3} data-testid="response-comment" />
            </div>

            <div>
              <Label className="text-sm text-slate-700">Modification de date (optionnel)</Label>
              <Input type="date" value={dateModifiee} onChange={e => setDateModifiee(e.target.value)}
                data-testid="response-date" />
            </div>

            <Button onClick={handleSubmit} disabled={!decision || submitting}
              className={`w-full gap-2 text-white font-semibold py-5 ${
                decision === 'valide' ? 'bg-green-600 hover:bg-green-700' :
                decision === 'refuse' ? 'bg-red-600 hover:bg-red-700' :
                'bg-slate-400'
              }`} data-testid="submit-response-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> :
               decision === 'valide' ? <CheckCircle2 className="w-4 h-4" /> :
               decision === 'refuse' ? <XCircle className="w-4 h-4" /> : null}
              Valider la réponse
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-4">CAPEX by Wasabi Business — Pilotage Agenceurs</p>
      </div>
    </div>
  );
}
